import React from 'react';
import { Container, Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import CarIcon from '../../../icons/car.svg?react';
import { object, string } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { StyledRadioInput, SelectInput } from '../../Helpers/Inputs';
import { Button, SvgIcon, ThemeProvider, createTheme } from '@mui/material';
import InputTheme from './Helpers/InputTheme';
import "./DrivingRecord.scss";

export default function DrivingRecord(props) {

  const formMethods = useForm({
    resolver: yupResolver(DrivingRecord.ValidationSchema),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState
  });

  const { handleSubmit, trigger } = formMethods;

  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
    trigger(field);
  };

  const buttonData = [
    {
      name: 'movingViolations',
      value: props.formState.movingViolations,
      title: 'Moving Violations',
      subtitle: 'I have had a moving violation in the last 2 years.',
      dropdownName: 'movingViolationCount',
      dropdownValue: props.formState.movingViolationCount,
      dropdownLabel: 'Number of Moving Violations',
      dropdownOption: [{ label: "1", value: "1" }, { label: "2", value: "2" }, { label: "3 or more", value: "3 or more" }],
    },
    {
      name: 'recklessDriving',
      value: props.formState.recklessDriving,
      title: 'Reckless Driving',
      subtitle: 'I have had a reckless driving conviction in the last 10 years.',
      dropdownName: 'recklessDrivingCount',
      dropdownValue: props.formState.recklessDrivingCount,
      dropdownLabel: 'Years since my conviction',
      dropdownOption: [{ label: "Less than 1 year", value: "Less than 1 year" }, { label: "1 – 2 years", value: "1 – 2 years" }, { label: "3 – 5 years", value: "3 – 5 years" }, { label: "6 – 10 years", value: "6 – 10 years" }],
    },
    {
      name: 'DUIOrDWI',
      value: props.formState.DUIOrDWI,
      title: 'DUI/DWI',
      subtitle: 'I have had a DUI/DWI conviction in the last 10 years.',
      dropdownName: 'DUIOrDWICount',
      dropdownValue: props.formState.DUIOrDWICount,
      dropdownLabel: 'Years since my conviction',
      dropdownOption: [{ label: "Less than 1 year", value: "Less than 1 year" }, { label: "1 – 2 years", value: "1 – 2 years" }, { label: "3 – 5 years", value: "3 – 5 years" }, { label: "6 – 10 years", value: "6 – 10 years" }],
    },
  ]

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...InputTheme(),
    })}>
      <Container className='driving-record personalize-step'>
        <Row className="justify-content-center pt-2">
          <div className={`icon-container background-color-16`}>
            <SvgIcon component={CarIcon} fontSize="large" color="primary" />
          </div>
        </Row>
        <Row className="justify-content-center mainTitle">
          <h4>Driving Record</h4>
        </Row>
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(props.onNext)} >
            <Row className="justify-content-center pt-2 form-profile" role="group" aria-labelledby="Select Personalizing Options" >
              {buttonData.map((d, index) =>
                <Col md='auto' key={index} className='driving-background'>
                  <Row className="px-4 pt-3">
                    <h5 className="driving-section-title">{d.title}</h5>
                  </Row>
                  <Row className="px-4 py-1">
                    <p className="formElementTitle">{d.subtitle}</p>
                  </Row>
                  <div className='pt-2 mb-0'>
                    <StyledRadioInput
                      name={d.name}
                      id={d.name}
                      value={d.value}
                      index={index}
                      options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                      onChange={(e) => handleValueChange(d.name, e)}
                      isBlankIcon={true}
                    />
                  </div>
                  {d.value === 'Yes' &&
                    <div className='mb-3'>
                      <SelectInput
                        name={d.dropdownName}
                        id={d.dropdownName}
                        value={d.dropdownValue}
                        onChange={(e) => handleValueChange(d.dropdownName, e.target.value)}
                        label={d.dropdownLabel}
                        selectOptions={d.dropdownOption}
                      />
                    </div>
                  }
                </Col>
              )}
            </Row>
            <Row className="pre-quote-nav-control">
              <Col className="text-start btn-back">
                <Button variant="outlined" size="medium" color="primary" className="px-4" onClick={props.onBack} >BACK</Button>
              </Col>
              <Col className="text-end">
                <Button variant="contained" size="medium" color="primary" type="submit" className="px-4">NEXT</Button>
              </Col>
            </Row>
          </Form>
        </FormProvider>
      </Container>
    </ThemeProvider>
  );
}

DrivingRecord.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

DrivingRecord.ValidationSchema = object().shape({
  movingViolations: string().required('This field is required.'),
  movingViolationCount: string().when('movingViolations', {
    is: 'Yes',
    then: (schema) => schema
      .required('This field is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  recklessDriving: string().required('This field is required.'),
  recklessDrivingCount: string().when('recklessDriving', {
    is: 'Yes',
    then: (schema) => schema
      .required('This field is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  DUIOrDWI: string().required('This field is required.'),
  DUIOrDWICount: string().when('DUIOrDWI', {
    is: 'Yes',
    then: (schema) => schema
      .required('This field is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
});

DrivingRecord.DefaultFormState = () => {
  return {
    movingViolations: 'No',
    recklessDriving: 'No',
    DUIOrDWI: 'No',
    movingViolationCount: '',
    recklessDrivingCount: '',
    DUIOrDWICount: '',
  };
};
